const CORPORATE_ACCOUNTS_URL = "/corporate-accounts/us-corporate";

const toQueryString = (query) => {
    if (!query) return "";
    const entries = Object.entries(query).filter(([, value]) => (
        value !== undefined && value !== null && `${value}`.trim() !== ""
    ));

    if (entries.length === 0) return "";

    return new URLSearchParams(entries).toString();
};

export const corporateAccountsEndpoints = (builder) => ({
    getCorporateApplications: builder.query({
        query: ({ query } = {}) => {
            const qs = toQueryString(query);
            return {
                url: `${CORPORATE_ACCOUNTS_URL}/applications${qs ? `?${qs}` : ""}`,
                method: "get",
            };
        },
        providesTags: ["Corporate Accounts"],
    }),

    getCorporateApplication: builder.query({
        query: ({ applicationId, query } = {}) => {
            const qs = toQueryString(query);
            return {
                url: `${CORPORATE_ACCOUNTS_URL}/applications/${applicationId}${qs ? `?${qs}` : ""}`,
                method: "get",
            };
        },
        providesTags: ["Corporate Accounts"],
    }),

    updateCorporateApplication: builder.mutation({
        query: ({ applicationId, body } = {}) => ({
            url: `${CORPORATE_ACCOUNTS_URL}/applications/${applicationId}`,
            method: "patch",
            body: body ?? {},
        }),
        invalidatesTags: ["Corporate Accounts"],
    }),

    createCorporateApplication: builder.mutation({
        query: ({ userId, body } = {}) => ({
            url: `${CORPORATE_ACCOUNTS_URL}/users/${userId}/applications`,
            method: "post",
            body: body ?? {},
        }),
        invalidatesTags: ["Corporate Accounts"],
    }),

    getCorporateApplicationEligibility: builder.query({
        query: ({ applicationId } = {}) => ({
            url: `${CORPORATE_ACCOUNTS_URL}/applications/${applicationId}/eligibility`,
            method: "get",
        }),
        providesTags: ["Corporate Accounts"],
    }),

    getCorporateAdminDocuments: builder.query({
        query: ({ query } = {}) => {
            const qs = toQueryString(query);
            return {
                url: `/users/admin/documents${qs ? `?${qs}` : ""}`,
                method: "get",
            };
        },
        providesTags: ["Corporate Accounts"],
    }),

    approveCorporateDocument: builder.mutation({
        query: ({ documentId, userId } = {}) => ({
            url: `/users/admin/confirm-doc`,
            method: "post",
            body: { documentId, userId },
        }),
        invalidatesTags: ["Corporate Accounts"],
    }),

    uploadCorporateApplicationDocument: builder.mutation({
        query: ({ applicationId, body } = {}) => ({
            url: `${CORPORATE_ACCOUNTS_URL}/applications/${applicationId}/documents`,
            method: "post",
            body: body ?? {},
        }),
        invalidatesTags: ["Corporate Accounts"],
    }),

    approveCorporateApplication: builder.mutation({
        query: ({ applicationId, query, body } = {}) => {
            const qs = toQueryString(query);
            return {
                url: `${CORPORATE_ACCOUNTS_URL}/applications/${applicationId}/approve${qs ? `?${qs}` : ""}`,
                method: "post",
                body: body ?? {},
            };
        },
        invalidatesTags: ["Corporate Accounts"],
    }),

    rejectCorporateApplication: builder.mutation({
        query: ({ applicationId, body } = {}) => ({
            url: `${CORPORATE_ACCOUNTS_URL}/applications/${applicationId}/reject`,
            method: "post",
            body: body ?? {},
        }),
        invalidatesTags: ["Corporate Accounts"],
    }),

    abandonCorporateApplication: builder.mutation({
        query: ({ applicationId, body } = {}) => ({
            url: `${CORPORATE_ACCOUNTS_URL}/applications/${applicationId}/abandon`,
            method: "post",
            body: body ?? {},
        }),
        invalidatesTags: ["Corporate Accounts"],
    }),

    submitCorporateVerifications: builder.mutation({
        query: ({ applicationId, query } = {}) => {
            const qs = toQueryString(query);
            return {
                url: `${CORPORATE_ACCOUNTS_URL}/applications/${applicationId}/submit-verifications${qs ? `?${qs}` : ""}`,
                method: "post",
            };
        },
        invalidatesTags: ["Corporate Accounts"],
    }),

    getCorporateApplicationProvisionStatus: builder.query({
        query: ({ applicationId } = {}) => ({
            url: `${CORPORATE_ACCOUNTS_URL}/applications/${applicationId}/provision/status`,
            method: "get",
        }),
        providesTags: ["Corporate Accounts"],
    }),

    syncCorporateApplicationProvision: builder.mutation({
        query: ({ applicationId } = {}) => ({
            url: `${CORPORATE_ACCOUNTS_URL}/applications/${applicationId}/provision/sync`,
            method: "post",
        }),
        invalidatesTags: ["Corporate Accounts"],
    }),

    approveCorporateW8bene: builder.mutation({
        query: ({ applicationId, query, body } = {}) => {
            const qs = toQueryString(query);
            return {
                url: `${CORPORATE_ACCOUNTS_URL}/applications/${applicationId}/w8bene/approve${qs ? `?${qs}` : ""}`,
                method: "post",
                body: body ?? {},
            };
        },
        invalidatesTags: ["Corporate Accounts"],
    }),

    rejectCorporateW8bene: builder.mutation({
        query: ({ applicationId, body } = {}) => ({
            url: `${CORPORATE_ACCOUNTS_URL}/applications/${applicationId}/w8bene/reject`,
            method: "post",
            body: body ?? {},
        }),
        invalidatesTags: ["Corporate Accounts"],
    }),

    // Clears a W-8BEN-E manual-review hold (status back to IN_PROGRESS) so the
    // customer can finish, sign and submit. Body: { notes } (optional).
    unlockCorporateW8bene: builder.mutation({
        query: ({ applicationId, body } = {}) => ({
            url: `${CORPORATE_ACCOUNTS_URL}/applications/${applicationId}/w8bene/unlock`,
            method: "post",
            body: body ?? {},
        }),
        invalidatesTags: ["Corporate Accounts"],
    }),

    getCorporateW8bene: builder.query({
        query: ({ applicationId } = {}) => ({
            url: `${CORPORATE_ACCOUNTS_URL}/applications/${applicationId}/w8bene`,
            method: "get",
        }),
        providesTags: ["Corporate Accounts"],
    }),

    startCorporateW8bene: builder.mutation({
        query: ({ applicationId } = {}) => ({
            url: `${CORPORATE_ACCOUNTS_URL}/applications/${applicationId}/w8bene/start`,
            method: "post",
            body: {},
        }),
        invalidatesTags: ["Corporate Accounts"],
    }),

    saveCorporateW8beneAnswers: builder.mutation({
        query: ({ applicationId, step, answers } = {}) => ({
            url: `${CORPORATE_ACCOUNTS_URL}/applications/${applicationId}/w8bene/answers`,
            method: "put",
            body: { step, answers },
        }),
        invalidatesTags: ["Corporate Accounts"],
    }),

    previewCorporateW8bene: builder.mutation({
        query: ({ applicationId } = {}) => ({
            url: `${CORPORATE_ACCOUNTS_URL}/applications/${applicationId}/w8bene/preview`,
            method: "post",
            body: {},
        }),
        invalidatesTags: ["Corporate Accounts"],
    }),

    signCorporateW8bene: builder.mutation({
        query: ({ applicationId, body } = {}) => ({
            url: `${CORPORATE_ACCOUNTS_URL}/applications/${applicationId}/w8bene/sign`,
            method: "post",
            body: body ?? {},
        }),
        invalidatesTags: ["Corporate Accounts"],
    }),

    submitCorporateW8bene: builder.mutation({
        query: ({ applicationId } = {}) => ({
            url: `${CORPORATE_ACCOUNTS_URL}/applications/${applicationId}/w8bene/submit`,
            method: "post",
            body: {},
        }),
        invalidatesTags: ["Corporate Accounts"],
    }),

    verifyCorporateCac: builder.mutation({
        query: ({ applicationId, query, body } = {}) => {
            const qs = toQueryString(query);
            return {
                url: `${CORPORATE_ACCOUNTS_URL}/applications/${applicationId}/cac/verify${qs ? `?${qs}` : ""}`,
                method: "post",
                body: body ?? {},
            };
        },
        invalidatesTags: ["Corporate Accounts"],
    }),

    manualVerifyCorporateCac: builder.mutation({
        query: ({ applicationId, query, body } = {}) => {
            const qs = toQueryString(query);
            return {
                url: `${CORPORATE_ACCOUNTS_URL}/applications/${applicationId}/cac/manual-verify${qs ? `?${qs}` : ""}`,
                method: "post",
                body: body ?? {},
            };
        },
        invalidatesTags: ["Corporate Accounts"],
    }),

    getCorporateApplicationTin: builder.query({
        query: ({ applicationId, tin, query } = {}) => {
            const qs = toQueryString(query);
            return {
                url: `${CORPORATE_ACCOUNTS_URL}/applications/${applicationId}/tin/${tin}${qs ? `?${qs}` : ""}`,
                method: "get",
            };
        },
        providesTags: ["Corporate Accounts"],
    }),

    getCorporateApplicationParties: builder.query({
        query: ({ applicationId } = {}) => ({
            url: `${CORPORATE_ACCOUNTS_URL}/applications/${applicationId}/parties`,
            method: "get",
        }),
        providesTags: ["Corporate Accounts"],
    }),

    saveCorporateApplicationParties: builder.mutation({
        query: ({ applicationId, additionalParties } = {}) => ({
            url: `${CORPORATE_ACCOUNTS_URL}/applications/${applicationId}/parties`,
            method: "post",
            body: { additionalParties: additionalParties ?? [] },
        }),
        invalidatesTags: ["Corporate Accounts"],
    }),

    deleteCorporateApplicationParty: builder.mutation({
        query: ({ applicationId, partyId } = {}) => ({
            url: `${CORPORATE_ACCOUNTS_URL}/applications/${applicationId}/parties/${partyId}`,
            method: "delete",
        }),
        invalidatesTags: ["Corporate Accounts"],
    }),

    getCorporateApplicationAgreements: builder.query({
        query: ({ applicationId } = {}) => ({
            url: `${CORPORATE_ACCOUNTS_URL}/applications/${applicationId}/agreements`,
            method: "get",
        }),
        providesTags: ["Corporate Accounts"],
    }),

    acceptCorporateApplicationAgreements: builder.mutation({
        query: ({ applicationId, body } = {}) => ({
            url: `${CORPORATE_ACCOUNTS_URL}/applications/${applicationId}/accept-agreements`,
            method: "post",
            body: body ?? {},
        }),
        invalidatesTags: ["Corporate Accounts"],
    }),

    createCorporateAccount: builder.mutation({
        query: ({ applicationId } = {}) => ({
            url: `${CORPORATE_ACCOUNTS_URL}/applications/${applicationId}/create-account`,
            method: "post",
            body: {},
        }),
        invalidatesTags: ["Corporate Accounts"],
    }),

    getPendingBusinessKybAttempts: builder.query({
        query: ({ query } = {}) => {
            const qs = toQueryString(query);
            return {
                url: `/settings/pending-business-kyb-attempts${qs ? `?${qs}` : ""}`,
                method: "get",
            };
        },
        providesTags: ["Corporate Accounts"],
    }),

    retryPendingBusinessKybAttempts: builder.mutation({
        query: () => ({
            url: `/settings/pending-business-kyb-attempts`,
            method: "patch",
        }),
        invalidatesTags: ["Corporate Accounts"],
    }),

    // --- Corporate user lookup (create-application wizard, Part B · Step 0) ---
    lookupAdminUsers: builder.query({
        query: ({ query } = {}) => {
            const qs = toQueryString(query);
            return {
                url: `/users/admin/users${qs ? `?${qs}` : ""}`,
                method: "get",
            };
        },
        providesTags: ["Corporate Accounts"],
    }),

    lookupAdminUsersFilter: builder.query({
        query: ({ query } = {}) => {
            const qs = toQueryString(query);
            return {
                url: `/users/admin/users-filter${qs ? `?${qs}` : ""}`,
                method: "get",
            };
        },
        providesTags: ["Corporate Accounts"],
    }),

    lookupAdminKyc: builder.query({
        query: ({ query } = {}) => {
            const qs = toQueryString(query);
            return {
                url: `/users/admin/kyc${qs ? `?${qs}` : ""}`,
                method: "get",
            };
        },
        providesTags: ["Corporate Accounts"],
    }),

});
